import { ChordLib, getDefaultProgressionForScale } from '@tunasong/music-lib'
import type { Chord } from '@tunasong/music-lib'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'

/**
 * Melody Writer standalone-route URL-param parsers. Mirrors the Cadence Lab
 * parsers but accepts ≥1 chord (Melody Writer has no MIN_CHORDS gate beyond
 * the inner drawer's `chords.length === 0` check).
 *
 * Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md §3.2
 */

const SUPPORTED_SCALE_TYPES: readonly ScaleType[] = [
  'major',
  'minor',
  'dorian',
  'mixolydian',
  'phrygian',
  'lydian',
  'aeolian',
  'locrian',
]

const isSupportedScaleType = (value: string): value is ScaleType =>
  (SUPPORTED_SCALE_TYPES as readonly string[]).includes(value)

const VALID_NOTE_NAMES = new Set<string>([
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B',
  'Dbb', 'Db', 'Ebb', 'Eb', 'Fb', 'E#', 'Gb', 'Abb', 'Ab', 'Bbb', 'Bb', 'Cb', 'B#',
])

const isValidNoteName = (value: string): value is NoteName => VALID_NOTE_NAMES.has(value)

export const DEFAULT_MELODY_WRITER_SCALE: Scale = { root: 'C', type: 'major' }

export const parseMelodyWriterScaleParam = (raw: string | null): Scale => {
  if (raw === null || raw === '') {
    return DEFAULT_MELODY_WRITER_SCALE
  }
  const parts = raw.split('-')
  if (parts.length !== 2) {
    return DEFAULT_MELODY_WRITER_SCALE
  }
  const [rootRaw, typeRaw] = parts
  if (!isValidNoteName(rootRaw)) {
    return DEFAULT_MELODY_WRITER_SCALE
  }
  if (!isSupportedScaleType(typeRaw)) {
    return DEFAULT_MELODY_WRITER_SCALE
  }
  return { root: rootRaw, type: typeRaw }
}

export const parseMelodyWriterChordsParam = (raw: string | null, scale: Scale): Chord[] => {
  if (raw === null || raw === '') {
    return getDefaultProgressionForScale(scale)
  }
  const tokens = raw.split(',').map(t => t.trim()).filter(t => t.length > 0)
  if (tokens.length === 0) {
    return getDefaultProgressionForScale(scale)
  }
  const parsed: Chord[] = []
  for (const token of tokens) {
    const chord = ChordLib.fromName(token)
    if (!chord) {
      return getDefaultProgressionForScale(scale)
    }
    parsed.push(chord)
  }
  return parsed
}
